import { FC, Fragment, useEffect, useRef, useState } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { useToast } from "../../../hooks"
import { formatDateTimeFromTimestamp } from "../../../utils"
import bbApi from "../../../../api"
import { CancellationRequest } from "../../../../api/cancel-request"

const CancelSubscription: FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [reason, setReason] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [cancellationRequest, setCancellationRequest] =
    useState<CancellationRequest | null>(null)
  const cancelButtonRef = useRef(null)
  const { pushErrorToast, pushSuccessToast } = useToast()

  useEffect(() => {
    const fetchCancellationRequest = async () => {
      try {
        const response = await bbApi.cancelRequest.get()
        setCancellationRequest(response.cancellation_request)
      } catch (error) {
        // Silently fail - user can still submit a new request
      } finally {
        setIsLoading(false)
      }
    }
    fetchCancellationRequest()
  }, [])

  const handleOpenDialog = () => setIsDialogOpen(true)
  const handleCloseDialog = () => {
    if (!isSubmitting) {
      setIsDialogOpen(false)
      setReason("")
    }
  }

  const handleSubmit = async () => {
    if (!reason.trim()) {
      pushErrorToast("Please provide a reason for cancellation.")
      return
    }

    setIsSubmitting(true)
    try {
      await bbApi.cancelRequest.submit({ reason: reason.trim() })
      pushSuccessToast("Your cancellation request has been submitted.")
      setIsDialogOpen(false)
      setReason("")
      // Refresh the cancellation request state
      const response = await bbApi.cancelRequest.get()
      setCancellationRequest(response.cancellation_request)
    } catch (error) {
      pushErrorToast("Failed to submit cancellation request. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClearRequest = async () => {
    setIsSubmitting(true)
    try {
      await bbApi.cancelRequest.clear()
      pushSuccessToast("Your cancellation request has been withdrawn.")
      setCancellationRequest(null)
    } catch (error) {
      pushErrorToast(
        "Failed to withdraw cancellation request. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="pt-12 pb-12">
        <div className="flex items-center justify-between pb-4 border-b border-neutral-200">
          <div className="text-lg font-bold">Cancel Subscription</div>
        </div>
        <div className="pt-6">
          <p className="text-neutral-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (cancellationRequest) {
    return (
      <div className="pt-12 pb-12">
        <div className="flex items-center justify-between pb-4 border-b border-neutral-200">
          <div className="text-lg font-bold">Cancel Subscription</div>
        </div>
        <div className="pt-6">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-amber-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  Cancellation Requested
                </h3>
                <p className="mt-1 text-sm text-amber-700">
                  Your cancellation request was submitted on{" "}
                  {formatDateTimeFromTimestamp(
                    cancellationRequest.timestamp,
                    "dd LLLL yyyy"
                  )}
                  . Our team will process your request shortly.
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClearRequest}
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Withdrawing..." : "Withdraw Request"}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-12 pb-12">
      <div className="flex items-center justify-between pb-4 border-b border-neutral-200">
        <div className="text-lg font-bold">Cancel Subscription</div>
      </div>
      <div className="pt-6">
        <p className="text-neutral-600 mb-4">
          If you wish to cancel your subscription, please click the button below
          and provide a reason.
        </p>
        <button
          type="button"
          onClick={handleOpenDialog}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          Cancel Subscription
        </button>
      </div>

      <Transition.Root show={isDialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          initialFocus={cancelButtonRef}
          onClose={handleCloseDialog}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all rounded-md sm:my-8 sm:w-full sm:max-w-xl">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-8 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                        <Dialog.Title
                          as="h3"
                          className="text-xl font-medium leading-6 text-base-dark"
                        >
                          Cancel Subscription
                        </Dialog.Title>
                        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3">
                          <p className="text-sm text-blue-700">
                            Note: This cancellation will apply to all users that
                            use this service.
                          </p>
                        </div>
                        <div className="mt-4">
                          <label
                            htmlFor="cancel-reason"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Reason for cancellation
                          </label>
                          <textarea
                            id="cancel-reason"
                            rows={4}
                            className="appearance-none block w-full px-3 py-2.5 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Please tell us why you want to cancel..."
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr className="text-gray-300 mt-6" />
                  <div className="bg-white px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      className="inline-flex justify-center items-center rounded-md border border-transparent bg-red-500 hover:bg-red-700 px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-0 sm:ml-3 sm:w-auto sm:text-md disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Request"}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex justify-center items-center rounded-md bg-gray-100 px-4 py-2 text-default font-medium text-gray-700 shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-0 sm:mt-0 sm:ml-3 sm:w-auto sm:text-md"
                      onClick={handleCloseDialog}
                      ref={cancelButtonRef}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  )
}

export default CancelSubscription
